/**
 * The documents table, its filter bar, its selection and its bulk actions — shared by the
 * documents screen and by a collection's screen, which is the same list with `collection`
 * pinned. Two implementations of this would drift the first time a facet was added.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../lib/api";
import { ago, bytes, shortHash } from "../lib/format";
import { useListKeys } from "../lib/useListKeys";
import { useListQuery } from "../lib/useListQuery";
import type {
  BulkResult,
  CollectionSummary,
  DocumentList,
  DocumentSummary,
  SavedView,
  TagSummary,
} from "../lib/types";
import { BulkBar } from "./BulkBar";
import { CollectionPicker } from "./CollectionPicker";
import { DataTable } from "./DataTable";
import { FilterBar, type Facet } from "./FilterBar";
import { Chip, Empty, Failed, Panel, STATUS_TONE, Button } from "./ui";

const PAGE = 50;
const STATUSES = ["succeeded", "processing", "queued", "failed", "stale", "unknown"] as const;
const TYPES = ["loss_run", "sov", "acord", "financial", "other"] as const;

const busy = (documents: DocumentSummary[]) =>
  documents.some((doc) => doc.status === "queued" || doc.status === "processing");

export function DocumentsList({
  pinnedCollection,
  onLoaded,
  empty,
}: {
  pinnedCollection?: string;
  /** The page that just loaded, so a caller's metric strip counts it without issuing a
   *  second request. */
  onLoaded?: (documents: DocumentSummary[]) => void;
  /** Overrides the unfiltered empty state. A collection page has no uploader of its own
   *  unless it mounts one, so "Drop a file above" is a lie there — the screen that knows
   *  what is above the table is the screen that should write this. */
  empty?: { title: string; hint: string; action: { label: string; onClick: () => void } };
}) {
  const query = useListQuery();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [failures, setFailures] = useState<{ id: string; label: string; error: string }[]>([]);
  const [tagging, setTagging] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [pendingCollections, setPendingCollections] = useState<string[]>([]);
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");

  const search = new URLSearchParams(query.toQueryString());
  search.set("limit", String(PAGE));
  if (pinnedCollection) search.append("collection", pinnedCollection);
  const queryString = search.toString();

  const listing = useQuery({
    queryKey: ["documents", queryString],
    queryFn: () => get<DocumentList>(`/v1/documents?${queryString}`),
    refetchInterval: (q) => (busy(q.state.data?.documents ?? []) ? 3_000 : false),
  });
  const collections = useQuery({
    queryKey: ["collections"],
    queryFn: () => get<{ collections: CollectionSummary[] }>("/v1/collections"),
  });
  const tags = useQuery({ queryKey: ["tags"], queryFn: () => get<{ tags: TagSummary[] }>("/v1/tags") });
  const views = useQuery({ queryKey: ["views"], queryFn: () => get<{ views: SavedView[] }>("/v1/views") });

  const documents = listing.data?.documents ?? [];
  const cursor = query.get("cursor");

  // The ids on the next page are different documents; carrying a selection across pages
  // produces a bulk action over rows nobody can see.
  useEffect(() => {
    setSelected(new Set());
    setFailures([]);
  }, [cursor]);

  // onLoaded is depended on ONLY through a ref, never in the effect's dependency array.
  // Documents.tsx passes an inline lambda that gets a new identity every render; that
  // render is itself triggered by the useState the lambda writes into — put the lambda
  // in the dependency array and the effect refires every time it changes identity,
  // which is every render, which is an infinite loop.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    if (listing.data) onLoadedRef.current?.(listing.data.documents);
  }, [listing.data]);

  const bulk = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      post<BulkResult>("/v1/documents/bulk", { ids: [...selected], ...body }),
    onSuccess: (result) => {
      setFailures(
        result.failed.map((failure) => ({
          id: failure.id,
          label: documents.find((d) => d.id === failure.id)?.filename ?? failure.id,
          error: failure.error,
        })),
      );
      if (result.failed.length === 0) setSelected(new Set());
      // A correction, a tag or a delete invalidates rows, exceptions and review together.
      void client.invalidateQueries();
    },
  });

  const facets: readonly Facet[] = [
    { kind: "search", key: "q", placeholder: "name, collection, tag" },
    {
      kind: "multi",
      key: "collection",
      label: "collection",
      options: (collections.data?.collections ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        ...(c.color ? { color: c.color } : {}),
      })),
    },
    {
      kind: "multi",
      key: "tag",
      label: "tag",
      options: (tags.data?.tags ?? []).map((t) => ({ value: t.name, label: t.name })),
    },
    {
      kind: "multi",
      key: "status",
      label: "status",
      options: STATUSES.map((value) => ({ value, label: value })),
    },
    { kind: "multi", key: "type", label: "kind", options: TYPES.map((value) => ({ value, label: value })) },
    { kind: "toggle", key: "untagged", label: "untagged" },
    { kind: "toggle", key: "uncollected", label: "uncollected" },
    { kind: "sort", keys: ["created_at", "filename", "byte_size"] },
  ];

  const { focused } = useListKeys({
    rows: documents,
    rowKey: (doc) => doc.id,
    onOpen: (doc) => navigate(`/documents/${doc.id}`),
    selection: { selected, onChange: setSelected },
    onClearFilters: () => query.clear(),
  });

  const filtered = query.toViewQuery() !== "";

  return (
    <Panel fill title="documents">
      <FilterBar
        facets={facets}
        views={{
          list: views.data?.views ?? [],
          onSave: (name) => {
            void post("/v1/views", { name, query: query.toViewQuery() }).then(() =>
              client.invalidateQueries({ queryKey: ["views"] }),
            );
          },
          onDelete: (id) => {
            // `del`, not a raw fetch: the credential rides on the cookie via
            // `credentials: "include"`, but a raw `fetch` here would still skip the
            // shared `failure()` handling — the 401 funnel that signs the console out on
            // an expired session — and every other error-body parsing `del` already does.
            void del(`/v1/views/${id}`).then(() =>
              client.invalidateQueries({ queryKey: ["views"] }),
            );
          },
        }}
      />

      <BulkBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        failures={failures}
        actions={[
          {
            // Opens the popover below rather than calling the API directly — the only
            // one of the three actions that needs input from the person first.
            label: "tag",
            permission: "documents:write",
            run: async () => setTagging(true),
          },
          {
            // Same shape as "tag": it opens the picker below, because the API needs to be
            // told *which* collection and only a person can say.
            label: "add to collection",
            permission: "documents:write",
            run: async () => setCollecting(true),
          },
          {
            label: "reparse",
            permission: "jobs:retry",
            run: async () => {
              await bulk.mutateAsync({ action: "reparse" });
            },
          },
          {
            label: "delete",
            permission: "documents:delete",
            run: async () => {
              await bulk.mutateAsync({ action: "delete" });
            },
          },
        ]}
      />

      {tagging && (
        // A minimal Enter-to-commit input, not `TagInput` (Task 29): Task 27 lands
        // before Task 29 creates it, and this popover needs only "type, Enter, Apply" —
        // no existing-tag suggestions, no per-tag remove. If a fourth caller ever needs
        // the full behaviour, that is the trigger to hoist a shared component; two
        // call sites with slightly different needs is not that trigger yet.
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hair bg-hair-soft px-3 py-2">
          <span className="font-mono text-[10.5px] tracking-[0.14em] text-faint uppercase">
            add tags
          </span>
          {pendingTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 bg-hair-soft px-2 py-0.5 font-mono text-[10.5px] uppercase"
            >
              {tag}
            </span>
          ))}
          <input
            aria-label="add a tag"
            value={tagDraft}
            onChange={(event) => setTagDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                const next = tagDraft.trim();
                if (next !== "" && !pendingTags.includes(next)) {
                  setPendingTags((prev) => [...prev, next]);
                }
                setTagDraft("");
              }
            }}
            className="min-w-24 border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
          />
          <Button
            variant="primary"
            disabled={pendingTags.length === 0 || bulk.isPending}
            onClick={async () => {
              await bulk.mutateAsync({ action: "add_tags", tags: pendingTags });
              setTagging(false);
              setPendingTags([]);
            }}
          >
            apply
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setTagging(false);
              setPendingTags([]);
              setTagDraft("");
            }}
          >
            cancel
          </Button>
        </div>
      )}

      {collecting && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hair bg-hair-soft px-3 py-2">
          <span className="font-mono text-[10.5px] tracking-[0.14em] text-faint uppercase">
            add to
          </span>
          <CollectionPicker
            value={pendingCollections}
            options={collections.data?.collections ?? []}
            onChange={setPendingCollections}
          />
          <Button
            variant="primary"
            disabled={pendingCollections.length === 0 || bulk.isPending}
            onClick={async () => {
              await bulk.mutateAsync({
                action: "add_collections",
                collections: pendingCollections,
              });
              setCollecting(false);
              setPendingCollections([]);
            }}
          >
            apply
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setCollecting(false);
              setPendingCollections([]);
            }}
          >
            cancel
          </Button>
        </div>
      )}

      {listing.isError ? (
        <Failed error={listing.error} what="documents" />
      ) : !listing.isPending && documents.length === 0 ? (
        filtered ? (
          <Empty
            title="nothing matches these filters"
            hint="Narrower than you meant, or nothing has arrived yet under them."
            action={{ label: "clear filters", onClick: () => query.clear() }}
          />
        ) : (
          <Empty
            title={empty?.title ?? "nothing here yet"}
            hint={
              empty?.hint ??
              "Drop a file above. It is sniffed, stored under its content hash, and queued."
            }
            action={
              empty?.action ?? {
                label: "upload a document",
                onClick: () => navigate("/documents#upload"),
              }
            }
          />
        )
      ) : (
        <DataTable
          fill
          table="documents"
          loading={listing.isPending}
          rows={documents}
          rowKey={(doc) => doc.id}
          selection={{ selected, onChange: setSelected }}
          onRowClick={(doc) => navigate(`/documents/${doc.id}`)}
          columns={[
            {
              key: "document",
              header: "document",
              width: 280,
              render: (doc) => (
                <Link
                  to={`/documents/${doc.id}`}
                  title={doc.filename ?? undefined}
                  className="block truncate font-medium hover:text-gold"
                >
                  {doc.filename ?? <span className="text-faint italic">unnamed</span>}
                </Link>
              ),
            },
            {
              key: "labels",
              header: "labels",
              width: 240,
              render: (doc) => (
                <span className="flex flex-wrap gap-1">
                  {doc.collections.map((collection) => (
                    <Chip key={collection.id} tone="gold">
                      {collection.name}
                    </Chip>
                  ))}
                  {doc.tags.map((tag) => (
                    <Chip key={tag.id}>{tag.name}</Chip>
                  ))}
                </span>
              ),
            },
            {
              key: "status",
              header: "status",
              width: 130,
              render: (doc) => (
                <Chip tone={STATUS_TONE[doc.status] ?? "mut"} dot>
                  {doc.status}
                </Chip>
              ),
            },
            {
              key: "kind",
              header: "kind",
              width: 110,
              cellClass: "font-mono text-[11.5px] text-dim",
              render: (doc) => doc.doc_type ?? <span className="text-faint">—</span>,
            },
            {
              key: "size",
              header: "size",
              width: 90,
              align: "end",
              cellClass: "tnum font-mono text-[11.5px]",
              render: (doc) => bytes(doc.byte_size),
            },
            {
              key: "digest",
              header: "digest",
              width: 130,
              cellClass: "font-mono text-[11.5px] text-faint",
              render: (doc) => shortHash(doc.sha256),
            },
            {
              key: "added",
              header: "added",
              width: 120,
              cellClass: "text-[12px] text-dim",
              render: (doc) => ago(doc.created_at),
            },
          ]}
        />
      )}

      <div className="flex shrink-0 items-center justify-between border-t border-hair px-3 py-2">
        <span className="eyebrow">row {documents.length === 0 ? 0 : focused + 1} of {documents.length}</span>
        <span className="flex gap-2">
          {cursor && (
            <button
              type="button"
              onClick={() => query.set({ cursor: null })}
              className="font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase hover:text-gold"
            >
              ← newest
            </button>
          )}
          <button
            type="button"
            disabled={!listing.data?.next_cursor}
            onClick={() => query.set({ cursor: listing.data?.next_cursor ?? null })}
            className="font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase hover:text-gold disabled:opacity-40"
          >
            older →
          </button>
        </span>
      </div>
    </Panel>
  );
}
